import type { DashboardDataset, TargetConfig } from '../types';

const DB_NAME = 'business-dashboard';
const STORE_NAME = 'datasets';
const DATASET_KEY = 'current';
const TARGET_KEY = 'business-dashboard-targets';

const openDatabase = () =>
  new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(STORE_NAME)) {
        request.result.createObjectStore(STORE_NAME);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });

export const saveDataset = async (dataset: DashboardDataset) => {
  const database = await openDatabase();
  await new Promise<void>((resolve, reject) => {
    const transaction = database.transaction(STORE_NAME, 'readwrite');
    transaction.objectStore(STORE_NAME).put(dataset, DATASET_KEY);
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
  });
  database.close();
};

export const loadDataset = async () => {
  const database = await openDatabase();
  const result = await new Promise<DashboardDataset | undefined>((resolve, reject) => {
    const request = database.transaction(STORE_NAME, 'readonly').objectStore(STORE_NAME).get(DATASET_KEY);
    request.onsuccess = () => resolve(request.result as DashboardDataset | undefined);
    request.onerror = () => reject(request.error);
  });
  database.close();
  return result;
};

export const clearDataset = async () => {
  const database = await openDatabase();
  await new Promise<void>((resolve, reject) => {
    const transaction = database.transaction(STORE_NAME, 'readwrite');
    transaction.objectStore(STORE_NAME).delete(DATASET_KEY);
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
  });
  database.close();
};

export const loadTargets = (fallback: TargetConfig): TargetConfig => {
  try {
    const saved = localStorage.getItem(TARGET_KEY);
    return saved ? { ...fallback, ...(JSON.parse(saved) as Partial<TargetConfig>) } : fallback;
  } catch {
    return fallback;
  }
};

export const saveTargets = (targets: TargetConfig) => {
  localStorage.setItem(TARGET_KEY, JSON.stringify(targets));
};
